import type { Currency } from '../money';
import { transitiveChain } from '../records/transitive';
import { inForceThrough } from '../linkRow';
import { isoDay } from '../zonedTime';
import type { EnforcementMode } from './viewer';

/**
 * Approval rules, canApprove, standing manager authority, delegation and
 * succession (piece 8; decisions 0020, 0079, 0144, 0176, 0192). The entity
 * lives here because earlier phases consume it; only its editor belongs to
 * Settings.
 */
export const APPROVER_KINDS = ['SystemRole', 'Employment', 'DepartmentHead', 'RequesterManager'] as const;
export type ApproverKind = (typeof APPROVER_KINDS)[number];

/** How far up the chain a manager's standing authority reaches: an Organization-scoped SettingValue row, seeded 0, and 0 means the entire chain. A change is audited and applies to no approval already made. */
export const CHAIN_DEPTH_KEY = 'approvals.chainDepth';
export const CHAIN_DEPTH_DEFAULT = 0;

/** A depth is a whole number of levels, zero for the entire chain; the field says so on itself. */
export function chainDepthProblem(value: unknown): string | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? null : 'the chain depth is a whole number of levels, and 0 reaches the entire chain';
}

export interface ApprovalRuleRow {
  id: string;
  documentType: string;
  departmentId: string | null;
  minAmount: Currency;
  maxAmount: Currency | null;
  stepOrder: number;
  approverKind: ApproverKind;
  approverSystemRoleId: string | null;
  approverEmploymentId: string | null;
  allowSelfApproval: boolean;
  isActive: boolean;
}

export class ApprovalRuleError extends Error {}

/** approverKind says which resolution a rule uses, and the ids on the row must agree with it (decision 0192). */
export function assertApproverColumns(rule: Pick<ApprovalRuleRow, 'approverKind' | 'approverSystemRoleId' | 'approverEmploymentId' | 'departmentId'>): void {
  const role = rule.approverSystemRoleId !== null;
  const employment = rule.approverEmploymentId !== null;
  if (role && employment) throw new ApprovalRuleError('a rule names a role or an employment, never both');
  switch (rule.approverKind) {
    case 'SystemRole':
      if (!role) throw new ApprovalRuleError('a SystemRole rule names approverSystemRoleId');
      return;
    case 'Employment':
      if (!employment) throw new ApprovalRuleError('an Employment rule names approverEmploymentId');
      return;
    case 'DepartmentHead':
      if (role || employment) throw new ApprovalRuleError('a DepartmentHead rule names neither approver column');
      if (rule.departmentId === null) throw new ApprovalRuleError('a DepartmentHead rule names the department whose head approves');
      return;
    case 'RequesterManager':
      if (role || employment) throw new ApprovalRuleError('a RequesterManager rule names neither approver column');
      return;
  }
}

function bandsOverlap(a: ApprovalRuleRow, b: ApprovalRuleRow): boolean {
  const aMax = a.maxAmount?.amount ?? Number.POSITIVE_INFINITY;
  const bMax = b.maxAmount?.amount ?? Number.POSITIVE_INFINITY;
  return a.minAmount.amount <= bMax && b.minAmount.amount <= aMax;
}

/** Overlapping bands within one documentType, step and department are refused at rule-save time, never discovered at approval time. */
export function assertNoOverlap(candidate: ApprovalRuleRow, existing: readonly ApprovalRuleRow[]): void {
  if (candidate.maxAmount && candidate.maxAmount.amount < candidate.minAmount.amount) throw new ApprovalRuleError('maxAmount is below minAmount');
  for (const other of existing) {
    if (other.id === candidate.id || !other.isActive || other.documentType !== candidate.documentType || other.stepOrder !== candidate.stepOrder) continue;
    if ((other.departmentId ?? null) !== (candidate.departmentId ?? null)) continue;
    if (bandsOverlap(candidate, other)) throw new ApprovalRuleError(`the band overlaps rule ${other.id} for ${other.documentType} step ${other.stepOrder}`);
  }
}

export interface ApprovalDocument {
  id: string;
  documentType: string;
  total: Currency;
  departmentId: string | null;
  /** The submitter's employment, whose manager a RequesterManager step routes to. */
  submitterEmploymentId: string;
  createdByPersonId: string;
}

/** The active rules whose documentType and band match the document, one per step in stepOrder; a department rule beats an organization-wide one at the same step. */
export function matchingRules(document: ApprovalDocument, rules: readonly ApprovalRuleRow[]): ApprovalRuleRow[] {
  const inBand = rules.filter((r) => {
    if (!r.isActive || r.documentType !== document.documentType) return false;
    if (r.departmentId !== null && r.departmentId !== document.departmentId) return false;
    const max = r.maxAmount?.amount ?? Number.POSITIVE_INFINITY;
    return document.total.amount >= r.minAmount.amount && document.total.amount <= max;
  });
  const bySteps = new Map<number, ApprovalRuleRow>();
  for (const rule of inBand.sort((a, b) => a.stepOrder - b.stepOrder)) {
    const held = bySteps.get(rule.stepOrder);
    if (!held || (held.departmentId === null && rule.departmentId !== null)) bySteps.set(rule.stepOrder, rule);
  }
  return [...bySteps.values()];
}

export interface EmploymentFacts {
  employmentId: string;
  personId: string;
  systemRoleId: string | null;
  /** Read off the EmploymentPosition row in force (decision 0079). */
  bossEmploymentId: string | null;
  isActive: boolean;
}

/** One row per redirect of an approver's queue to another employment, for a department or for all of them, over a dated span. */
export interface ApprovalDelegationRow {
  fromEmploymentId: string;
  toEmploymentId: string;
  departmentId: string | null;
  startDate: string | Date;
  endDate: string | Date | null;
}

/**
 * 🔒 What the End Employment window reads before End (decision 11740; T3608): each department the employment approves
 * for, keyed by its id or `all`, with the successor the chain gives, and the people at our own Company who may take it.
 */
export interface ApprovalHandOffPlan {
  departments: { key: string; departmentName: string | null; suggestedEmploymentId: string | null }[];
  people: { employmentId: string; name: string }[];
}

export interface ApprovalContext {
  mode: EnforcementMode;
  /** The organization's calendar day the routing is asked on, `YYYY-MM-DD`; a delegation ending on it still holds (decision 2860). */
  onDay: string;
  employments: ReadonlyMap<string, EmploymentFacts>;
  delegations: readonly ApprovalDelegationRow[];
  /** Department.headEmploymentId per department. */
  departmentHeads: ReadonlyMap<string, string | null>;
  /** approvals.chainDepth: how far standing authority runs up the chain; 0 is unlimited. */
  chainDepth: number;
  /** The holder of Organization.rootEmploymentId, the fallback approver (decision 0079). */
  rootEmploymentId: string;
  /**
   * 🔒 Told of a delegation loop the resolver met and stepped around (decision 10631): the caller reports it, and
   * the step routes as though the looping cover were not there, so the queue never faults.
   */
  onDelegationLoop?: (error: DelegationCycleError) => void;
}

export class DelegationCycleError extends Error {}

/** 🔒 A delegation is in force from its first day through its last (decision 2860), on the organization's day the caller hands in. */
const within = (row: ApprovalDelegationRow, onDay: string): boolean => {
  return isoDay(row.startDate) <= onDay && inForceThrough(row.endDate, onDay);
};

/** Delegations chain and resolve transitively with a cycle guard; a department delegation beats an all-departments one. */
export function resolveDelegate(employmentId: string, departmentId: string | null, delegations: readonly ApprovalDelegationRow[], onDay: string): string {
  const seen = new Set<string>([employmentId]);
  let current = employmentId;
  for (;;) {
    const live = delegations.filter((d) => d.fromEmploymentId === current && within(d, onDay) && (d.departmentId === null || d.departmentId === departmentId));
    const next = live.find((d) => d.departmentId !== null) ?? live[0];
    if (!next) return current;
    if (seen.has(next.toEmploymentId)) throw new DelegationCycleError(`delegation loops at ${next.toEmploymentId}`);
    seen.add(next.toEmploymentId);
    current = next.toEmploymentId;
  }
}

/**
 * A to B to A is rejected at save time rather than looping at resolution. 🔒 The walk runs on every day the
 * candidate is in force (decision 10631, from the review of batch OP1): its first day and each first day of
 * another row falling inside its span, since the rows in force change only where one begins. A cover from day 10
 * and a returning one from day 5 therefore meet on day 10 and are refused at the second save. `onDay` is the
 * earliest day asked, the candidate's own first day where the caller passes a later one.
 */
export function assertNoDelegationCycle(candidate: ApprovalDelegationRow, existing: readonly ApprovalDelegationRow[], onDay: string): void {
  const start = isoDay(candidate.startDate) > onDay ? isoDay(candidate.startDate) : onDay;
  const rows = [...existing, candidate];
  const days = new Set<string>([start]);
  for (const row of existing) {
    const begins = isoDay(row.startDate);
    if (begins > start && inForceThrough(candidate.endDate, begins)) days.add(begins);
  }
  for (const day of [...days].sort()) resolveDelegate(candidate.fromEmploymentId, candidate.departmentId, rows, day);
}

/**
 * The management chain above an employment, walked through bossEmploymentId with
 * a cycle guard; inactive bosses stay in it for succession to walk past. The walk
 * itself is the one shared transitive resolver (piece 10, task #106), so the
 * chain, company ownership and the Department tree cannot drift apart.
 */
export function managementChain(employmentId: string, employments: ReadonlyMap<string, EmploymentFacts>, maxDepth = 256): string[] {
  return transitiveChain(employmentId, (id) => employments.get(id)?.bossEmploymentId ?? null, {
    maxDepth,
    label: 'management chain',
    cycleError: (message) => new DelegationCycleError(message),
  });
}

/** Standing authority: is the viewer in the subordinate's chain within the depth cap? 0 means unlimited, and the cycle guard runs at any depth. */
export function hasStandingAuthorityOver(viewerEmploymentId: string, subordinateEmploymentId: string, ctx: Pick<ApprovalContext, 'employments' | 'chainDepth'>): boolean {
  if (viewerEmploymentId === subordinateEmploymentId) return false;
  const chain = managementChain(subordinateEmploymentId, ctx.employments);
  const reach = ctx.chainDepth > 0 ? chain.slice(0, ctx.chainDepth) : chain;
  return reach.includes(viewerEmploymentId);
}

/** Who a step names: the role, the employment, the department's head, or the submitter's manager, resolved at routing time. */
export type StepApprover = { kind: 'role'; systemRoleId: string } | { kind: 'employment'; employmentId: string } | { kind: 'unroutable'; reason: string };

export function resolveApprover(rule: ApprovalRuleRow, document: ApprovalDocument, ctx: ApprovalContext): StepApprover {
  const department = rule.departmentId ?? document.departmentId;
  const active = (id: string): boolean => ctx.employments.get(id)?.isActive === true;
  // 🔒 The stored delegation answers first; an approver still inactive after it is walked past, up the
  // management chain, each step read through the delegations again, ending at the root's holder (decision 10631).
  // 🔒 A loop the save guard did not see is stepped around, not thrown: the looping cover counts as absent and the
  // caller is told, so routing and the Pending Approvals queue never fault on it (decision 10631).
  const delegateOf = (employmentId: string): string => {
    try {
      return resolveDelegate(employmentId, department, ctx.delegations, ctx.onDay);
    } catch (error) {
      if (!(error instanceof DelegationCycleError)) throw error;
      ctx.onDelegationLoop?.(error);
      return employmentId;
    }
  };
  const redirected = (employmentId: string): StepApprover => {
    const target = delegateOf(employmentId);
    if (active(target)) return { kind: 'employment', employmentId: target };
    const above = [...managementChain(employmentId, ctx.employments), ...(ctx.rootEmploymentId ? [ctx.rootEmploymentId] : [])];
    for (const candidate of above) {
      const next = delegateOf(candidate);
      if (active(next)) return { kind: 'employment', employmentId: next };
      if (active(candidate)) return { kind: 'employment', employmentId: candidate };
    }
    return { kind: 'unroutable', reason: `employment ${target} is not active` };
  };
  switch (rule.approverKind) {
    case 'SystemRole':
      return rule.approverSystemRoleId ? { kind: 'role', systemRoleId: rule.approverSystemRoleId } : { kind: 'unroutable', reason: 'no role on a SystemRole rule' };
    case 'Employment':
      return rule.approverEmploymentId ? redirected(rule.approverEmploymentId) : { kind: 'unroutable', reason: 'no employment on an Employment rule' };
    case 'DepartmentHead': {
      const head = rule.departmentId ? (ctx.departmentHeads.get(rule.departmentId) ?? null) : null;
      return head ? redirected(head) : { kind: 'unroutable', reason: 'the department has no head' };
    }
    case 'RequesterManager': {
      const boss = ctx.employments.get(document.submitterEmploymentId)?.bossEmploymentId ?? null;
      return boss ? redirected(boss) : { kind: 'unroutable', reason: 'the submitter has no manager' };
    }
  }
}

/** The id a default route's one rule carries (decision 2180): no `ApprovalRule` row has it, so nothing reads it as one. */
export const DEFAULT_ROUTE_RULE_ID = 'default-route';

/**
 * 🔒 The route a kind takes when no `ApprovalRule` matches it (decision 2180):
 * one step to the nearest active person above the submitter's employment, at
 * any depth, and to the fallback role when nobody stands above them. 🔨 The
 * submitter never approves their own document, so an employment of theirs in
 * the chain is passed over. A fallback role the installation lacks answers a
 * step nobody can reach, which holds the document for an administrator.
 */
export function defaultRouteRule(document: ApprovalDocument, ctx: Pick<ApprovalContext, 'employments'>, fallbackRoleId: string | null): ApprovalRuleRow {
  const above = managementChain(document.submitterEmploymentId, ctx.employments).find((id) => {
    const facts = ctx.employments.get(id);
    return facts?.isActive === true && facts.personId !== document.createdByPersonId;
  });
  return {
    id: DEFAULT_ROUTE_RULE_ID,
    documentType: document.documentType,
    departmentId: null,
    minAmount: { amount: 0, currencyCode: document.total.currencyCode },
    maxAmount: null,
    stepOrder: 1,
    approverKind: above ? 'Employment' : 'SystemRole',
    approverSystemRoleId: above ? null : fallbackRoleId,
    approverEmploymentId: above ?? null,
    allowSelfApproval: false,
    isActive: true,
  };
}

export interface ApprovalDecision {
  stepOrder: number;
  byEmploymentId: string;
  outcome: 'Approved' | 'Rejected';
}

export type ApprovalState = { status: 'Pending'; awaitingStep: number } | { status: 'Approved' } | { status: 'Rejected'; atStep: number } | { status: 'Unroutable'; reason: string };

/** Steps resolve in order; the document stays Pending until the highest step approves, and a Reject at any step rejects outright. */
export function approvalState(document: ApprovalDocument, rules: readonly ApprovalRuleRow[], decisions: readonly ApprovalDecision[]): ApprovalState {
  const steps = matchingRules(document, rules);
  if (steps.length === 0) return { status: 'Unroutable', reason: 'no active rule matches the document' };
  const rejected = decisions.find((d) => d.outcome === 'Rejected');
  if (rejected) return { status: 'Rejected', atStep: rejected.stepOrder };
  for (const step of steps) {
    if (!decisions.some((d) => d.stepOrder === step.stepOrder && d.outcome === 'Approved')) return { status: 'Pending', awaitingStep: step.stepOrder };
  }
  return { status: 'Approved' };
}

export interface ApprovalViewer {
  employmentId: string;
  personId: string;
  systemRoleId: string | null;
  /**
   * 🔒 The capability keys the viewer's role holds (decision 0525). The gate
   * reads `admin.operations` off it to answer an unroutable document, and a
   * caller that passes none is answered as holding nothing.
   */
  capabilities?: readonly string[];
}

/**
 * 🔒 The capability that clears an unroutable document (decision 0525),
 * declared here because the pure resolver is what reads it. `ADMIN_CAPABILITY`
 * in the API is this same string.
 */
export const ADMIN_OPERATIONS_CAPABILITY = 'admin.operations';

export interface ApprovalAnswer {
  allowed: boolean;
  /** The capacity the audit line names: the named approver, the role, a manager acting for a subordinate, or an administrator clearing an unroutable document. */
  capacity: 'named' | 'role' | 'manager' | 'administrator' | null;
  step: number | null;
}

/**
 * 🔒 An administrator holds `admin.operations`, and clears a held document
 * whoever raised it (decision 1665, amending 0525): on a held document there is
 * nobody else to act, so refusing the raiser left a one-administrator shop with
 * nobody at all. The audit row names the self-clearance.
 */
function clearsUnroutable(viewer: ApprovalViewer): boolean {
  return viewer.capabilities?.includes(ADMIN_OPERATIONS_CAPABILITY) ?? false;
}

/**
 * 🔒 Whether a document is held for an administrator (decisions 0380, 0525,
 * 1665): no rule matches it at all, or the step it awaits stands against an
 * approver nobody can reach. The one reading the queue's held group and
 * `canApprove` both take, so the row's group and its buttons cannot disagree.
 */
export function heldForAdministrator(document: ApprovalDocument, rules: readonly ApprovalRuleRow[], decisions: readonly ApprovalDecision[], ctx: ApprovalContext): boolean {
  const state = approvalState(document, rules, decisions);
  if (state.status === 'Unroutable') return true;
  if (state.status !== 'Pending') return false;
  const rule = matchingRules(document, rules).find((r) => r.stepOrder === state.awaitingStep)!;
  return resolveApprover(rule, document, ctx).kind === 'unroutable';
}

/**
 * 🔒 Whether anybody active stands above an employment in the management
 * chain (decision 1692): the raiser of a held Quote clears it themselves only
 * where nobody does.
 */
export function nobodyAbove(employmentId: string, ctx: Pick<ApprovalContext, 'employments'>): boolean {
  return !managementChain(employmentId, ctx.employments).some((id) => ctx.employments.get(id)?.isActive === true);
}

/**
 * 🔒 Who clears a held document (decisions 0525, 1665, 1692). An administrator
 * clears it. A held Quote, which is how a margin floor holds one, goes up its
 * raiser's hierarchy first: anybody above the raiser in the management chain
 * clears it, and the raiser clears it only where nobody stands above them
 * (the product owner, 2026-10-03: *"Yes, if there is no one above them on the
 * hierarchy"*). Every other held type keeps decision 1665's answer.
 */
function clearsHeld(viewer: ApprovalViewer, document: ApprovalDocument, ctx: ApprovalContext, step: number | null): ApprovalAnswer {
  const admin = clearsUnroutable(viewer);
  if (document.documentType === 'Quote') {
    if (viewer.personId === document.createdByPersonId) {
      return admin && nobodyAbove(document.submitterEmploymentId, ctx) ? { allowed: true, capacity: 'administrator', step: null } : { allowed: false, capacity: null, step };
    }
    if (managementChain(document.submitterEmploymentId, ctx.employments).includes(viewer.employmentId)) return { allowed: true, capacity: 'manager', step: null };
  }
  return admin ? { allowed: true, capacity: 'administrator', step: null } : { allowed: false, capacity: null, step };
}

/** The reason the audit row of a self-clearance carries (decision 1665). */
export const SELF_CLEARANCE_REASON = 'held for an administrator and cleared by the administrator who raised it';

/** 🔒 A self-clearance: an administrator clearing a held document they raised themselves (decision 1665), which the audit row names. */
export function isSelfClearance(viewer: Pick<ApprovalViewer, 'personId'>, document: Pick<ApprovalDocument, 'createdByPersonId'>, answer: Pick<ApprovalAnswer, 'capacity'>): boolean {
  return answer.capacity === 'administrator' && viewer.personId === document.createdByPersonId;
}

/**
 * canApprove(viewer, document): the single resolver behind queue visibility,
 * Approve and Reject rendering, and which step is awaiting whom. The named
 * approver acts; so does anyone in the named approver's management chain
 * within the depth cap. The submitter never satisfies their own step unless
 * the rule allows it, and standing authority is never a self-approval loophole.
 *
 * 🔒 An unroutable document is an administrator's to clear (decision 0525):
 * a viewer holding `admin.operations` is answered allowed with the capacity
 * `administrator` and no step, whether nothing matched the amount at all
 * (decision 0527) or the matched rule's approver cannot be reached (decision
 * 0380). Nobody else can act on one. 🔒 The administrator who raised it clears
 * it too (decision 1665, amending 0525's refusal of the raiser): the held check
 * runs before the self-approval refusal, which still holds on every routable step.
 */
export function canApprove(viewer: ApprovalViewer, document: ApprovalDocument, rules: readonly ApprovalRuleRow[], decisions: readonly ApprovalDecision[], ctx: ApprovalContext): ApprovalAnswer {
  if (ctx.mode === 'sandbox') return { allowed: true, capacity: null, step: null };
  const state = approvalState(document, rules, decisions);
  // 🔒 No rule matched the amount at all, so there is no chain to satisfy and only an administrator clears it (decisions 0527, 0525).
  if (state.status === 'Unroutable') return clearsHeld(viewer, document, ctx, null);
  if (state.status !== 'Pending') return { allowed: false, capacity: null, step: null };
  const rule = matchingRules(document, rules).find((r) => r.stepOrder === state.awaitingStep)!;
  const approver = resolveApprover(rule, document, ctx);
  // 🔒 The matched rule's approver cannot be reached, so the step stands against nobody and an administrator clears it,
  // the one who raised it included (decisions 0380, 0525, 1665). It is asked before the raiser's refusal below.
  if (approver.kind === 'unroutable') return clearsHeld(viewer, document, ctx, rule.stepOrder);
  if (viewer.personId === document.createdByPersonId && !rule.allowSelfApproval) return { allowed: false, capacity: null, step: rule.stepOrder };
  if (approver.kind === 'role') {
    const holds = viewer.systemRoleId !== null && viewer.systemRoleId === approver.systemRoleId;
    return { allowed: holds, capacity: holds ? 'role' : null, step: rule.stepOrder };
  }
  if (approver.employmentId === viewer.employmentId) return { allowed: true, capacity: 'named', step: rule.stepOrder };
  if (hasStandingAuthorityOver(viewer.employmentId, approver.employmentId, ctx)) return { allowed: true, capacity: 'manager', step: rule.stepOrder };
  return { allowed: false, capacity: null, step: rule.stepOrder };
}

/**
 * Succession on deactivation: with no handoff the load goes to the departing
 * approver's boss, walking past inactive bosses to an active person or the
 * root, and the depth cap never applies. One delegation per department the
 * person approved for, dated from the deactivation; an explicit successor wins.
 */
export function successionDelegations(
  departingEmploymentId: string,
  departmentIds: readonly (string | null)[],
  ctx: Pick<ApprovalContext, 'employments' | 'rootEmploymentId'>,
  onDate: Date,
  namedSuccessors: ReadonlyMap<string | null, string> = new Map(),
): ApprovalDelegationRow[] {
  const chain = managementChain(departingEmploymentId, ctx.employments);
  const firstActive = chain.find((id) => ctx.employments.get(id)?.isActive) ?? ctx.rootEmploymentId;
  return departmentIds.map((departmentId) => ({
    fromEmploymentId: departingEmploymentId,
    toEmploymentId: namedSuccessors.get(departmentId) ?? firstActive,
    departmentId,
    startDate: onDate,
    endDate: null,
  }));
}
